import axios from "axios";
import { Toast } from "../../util/Toast";
import {
  GET_USER,
  GET_COINSELLER_UNIQUEID,
  CREATE_HOST_REQUEST,
  HOST_DISABLE,
  REMOVE_HOST,
} from "./types";
import { baseURL, key } from "../../util/Config";
import { ensureAgencyToken } from "../../util/agencySession";
import { SET_ADMIN } from "../admin/types";

const clearAgencyHostCaches = (agencyId) => {
  try {
    const keys = Object.keys(sessionStorage);
    keys.forEach((k) => {
      if (
        k.startsWith(`agency_hosts_cache_${agencyId}`) ||
        k.startsWith("agency_profile_cache_")
      ) {
        sessionStorage.removeItem(k);
      }
    });
  } catch {
    /* ignore */
  }
};

export const getUser = (agency_id, opts = {}) => (dispatch) => {
  const start = opts.start || 1;
  const limit = opts.limit || 200;
  const search = opts.search || "ALL";
  const startDate = opts.startDate || "ALL";
  const endDate = opts.endDate || "ALL";
  // Never hydrate host list from sessionStorage — lifetime currentCoin leaked as "Available Coin".
  clearAgencyHostCaches(agency_id);

  axios
    .get(
      `agency/agencyWiseHost?agencyId=${agency_id}&start=${start}&limit=${limit}&search=${search}&startDate=${startDate}&endDate=${endDate}&_=${Date.now()}`,
    )
    .then((res) => {
      if (res?.data?.status) {
        dispatch({
          type: GET_USER,
          payload: {
            agencyWiseHost: res?.data?.data,
            total: res?.data?.total,
          },
        });
      } else {
        Toast("error", res?.data?.message);
      }
    })
    .catch((error) => Toast("error", error.message));
};

export const getCoinSellerUniqueId = (search) => (dispatch) => {
  const q = encodeURIComponent(search || "");
  fetch(`${baseURL}user/getUsersUniqueIdForAgency?search=${q}`, {
    headers: {
      key,
    },
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then((data) => {
      dispatch({
        type: GET_COINSELLER_UNIQUEID,
        payload: data.data || [],
      });
    })
    .catch((error) => console.log("Fetch error:", error));
};

/**
 * Send host invite via chat (Accept/Decline in message box). Not a direct host add.
 */
export const createHost = (agencyId, uniqueId) => async (dispatch) => {
  if (!agencyId || !uniqueId) {
    Toast("error", "Missing agency or user id");
    return;
  }

  let token = "";
  try {
    token = await ensureAgencyToken(agencyId);
    if (token) {
      dispatch({ type: SET_ADMIN, payload: token });
    }
  } catch (e) {
    console.log("ensureAgencyToken", e?.message || e);
  }

  const headers = {};
  if (token) headers.Authorization = token;

  const postInvite = () =>
    axios.post(
      `agency/createHost?agencyId=${agencyId}&userId=${encodeURIComponent(uniqueId)}`,
      null,
      { headers }
    );

  try {
    let res;
    try {
      res = await postInvite();
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        try {
          token = await ensureAgencyToken(agencyId, { force: true });
          if (token) {
            dispatch({ type: SET_ADMIN, payload: token });
            headers.Authorization = token;
          }
        } catch (_) {
          /* backend may still allow key+agencyId */
        }
        res = await postInvite();
      } else {
        throw err;
      }
    }

    dispatch({
      type: CREATE_HOST_REQUEST,
      payload: res.data?.data,
    });
    if (res.data?.status) {
      clearAgencyHostCaches(agencyId);
      Toast(
        "success",
        res.data.message || "Invite sent. Host must Accept in chat."
      );
    } else {
      Toast("error", res.data?.message || "Failed to send invite");
    }
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error.message ||
      "Failed to send invite";
    Toast("error", msg);
  }
};

export const disableHost = (host) => (dispatch) => {
  axios
    .patch(`host/isBlock?hostId=${host._id}`)
    .then((res) => {
      dispatch({
        type: HOST_DISABLE,
        payload: { data: res.data.host, id: host._id },
      });
      Toast(
        "success",
        `${host.name} Is ${
          host.isBlock !== true ? "disable" : "UnDisable"
        } Successfully!`
      );
    })
    .catch((error) => {
      Toast("error", error.message);
    });
};

export const removeHost = (hostId, agencyId) => async (dispatch) => {
  if (!hostId || !agencyId) {
    Toast("error", "Missing host or agency");
    return Promise.resolve(false);
  }

  let token = "";
  try {
    token = await ensureAgencyToken(agencyId);
    if (token) {
      dispatch({ type: SET_ADMIN, payload: token });
    }
  } catch (e) {
    console.log("ensureAgencyToken", e?.message || e);
  }

  const headers = {};
  if (token) headers.Authorization = token;

  const doRemove = () =>
    axios.patch(
      `agency/removeHost?hostId=${hostId}&agencyId=${agencyId}`,
      {},
      { headers }
    );

  try {
    let res;
    try {
      res = await doRemove();
    } catch (err) {
      if (err?.response?.status === 401) {
        token = await ensureAgencyToken(agencyId, { force: true });
        if (token) {
          dispatch({ type: SET_ADMIN, payload: token });
          headers.Authorization = token;
          res = await doRemove();
        } else {
          throw err;
        }
      } else {
        throw err;
      }
    }
    if (res?.data?.status) {
      clearAgencyHostCaches(agencyId);
      dispatch({ type: REMOVE_HOST, payload: hostId });
      dispatch(getUser(agencyId, { start: 1, limit: 200, force: true }));
      Toast("success", res.data.message || "Host removed successfully.");
      return true;
    }
    Toast("error", res?.data?.message || "Failed to remove host");
    return false;
  } catch (error) {
    Toast("error", error.message || "Failed to remove host");
    return false;
  }
};
